import { z } from "zod";

// AI/ML Services Summary Schemas
export const AiMlServicesTrendPointSchema = z.object({
  date: z.string(),
  total_dbus: z.number(),
  total_cost_usd: z.number(),
  model_serving_dbus: z.number(),
  model_serving_cost_usd: z.number(),
  vector_search_dbus: z.number(),
  vector_search_cost_usd: z.number(),
  ai_gateway_dbus: z.number(),
  ai_gateway_cost_usd: z.number(),
});

// Summary response is now a direct array (no wrapper)
export const AiMlServicesSummaryResponseSchema = z.array(AiMlServicesTrendPointSchema);

export type AiMlServicesTrendPoint = z.infer<typeof AiMlServicesTrendPointSchema>;
export type AiMlServicesSummaryResponse = z.infer<typeof AiMlServicesSummaryResponseSchema>;

export interface AiMlServicesParams {
  start_date: string;
  end_date: string;
  granularity?: "day" | "week" | "month";
  workspace_id?: string;
}

// Unified AI/ML endpoint listing — one row shape covering all three product
// families (model_serving / vector_search / ai_gateway). `service` is the
// discriminator used for routing on row click. `entity_type` is the unified
// "Type" column / filter dimension: for model_serving rows it carries the
// served-entities entity type (FOUNDATION_MODEL, CUSTOM_MODEL, AI_FUNCTION,
// EXTERNAL_MODEL, FEATURE_SPEC); BE synthesizes `VECTOR_SEARCH` and
// `AI_GATEWAY` on the other two so the column is non-nullable.
export const UnifiedAIMLEndpointServiceSchema = z.enum([
  "model_serving",
  "vector_search",
  "ai_gateway",
]);

export const UnifiedAIMLEndpointItemSchema = z.object({
  service: UnifiedAIMLEndpointServiceSchema,
  endpoint_name: z.string(),
  endpoint_id: z.string().nullable(),
  entity_type: z.string(),
  total_dbus: z.number(),
  total_cost_usd: z.number(),
  // model_serving-only fields, null on the other product families
  created_by: z.string().nullable(),
  total_requests: z.number().nullable(),
  total_tokens: z.number().nullable(),
  unique_users: z.number().nullable(),
  // vector_search + ai_gateway fields, null on model_serving rows
  days_active: z.number().nullable(),
  first_seen: z.string().nullable(),
  last_activity: z.string().nullable(),
});

export const UnifiedAIMLEndpointsListResponseSchema = z.object({
  endpoints: z.array(UnifiedAIMLEndpointItemSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
});

export type UnifiedAIMLEndpointService = z.infer<typeof UnifiedAIMLEndpointServiceSchema>;
export type UnifiedAIMLEndpointItem = z.infer<typeof UnifiedAIMLEndpointItemSchema>;
export type UnifiedAIMLEndpointsListResponse = z.infer<
  typeof UnifiedAIMLEndpointsListResponseSchema
>;

export type UnifiedAIMLEndpointsSortBy =
  | "endpoint_name"
  | "total_cost_usd"
  | "total_dbus"
  | "service"
  | "entity_type";

// Filter values endpoint — returns distinct entity_types and workspace IDs
// that actually appear on rows with traffic in the date window, so the
// dropdowns can never offer a value the table can't show.
export const AimlEndpointFiltersResponseSchema = z.object({
  entity_types: z.array(z.string()),
  workspace_ids: z.array(z.string()),
});

export type AimlEndpointFiltersResponse = z.infer<typeof AimlEndpointFiltersResponseSchema>;

export interface AimlEndpointFiltersParams {
  start_date: string;
  end_date: string;
}

// Endpoint-name dropdown source — the full name set is too large to ship at
// once, so the FE searches server-side (substring `search`) and pages through
// matches. Mirrors the backend AIMLEndpointNamesResponse.
export const AimlEndpointNamesResponseSchema = z.object({
  items: z.array(z.string()),
  page: z.number(),
  size: z.number(),
  total: z.number(),
  total_pages: z.number(),
});

export type AimlEndpointNamesResponse = z.infer<typeof AimlEndpointNamesResponseSchema>;

export interface AimlEndpointNamesParams {
  start_date: string;
  end_date: string;
  search?: string;
  page?: number;
  size?: number;
  workspace_id?: string;
}

export interface UnifiedAIMLEndpointsParams {
  /** Comma-separated entity types (e.g. "FOUNDATION_MODEL,VECTOR_SEARCH"). */
  entity_type?: string;
  /** Exact endpoint name(s) to filter by (any-of). Serialized as repeated
   * `endpoint_name` query params; values come from the searchable names endpoint. */
  endpoint_name?: string[];
  start_date?: string;
  end_date?: string;
  workspace_id?: string;
  sort_by?: UnifiedAIMLEndpointsSortBy;
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

// Flattened Model Serving detail schema (no nested objects)
export const ModelServingEndpointDetailSchema = z.object({
  endpoint_name: z.string(),
  endpoint_id: z.string().nullable(),
  service: z.string(),
  entity_type: z.string().nullable(),
  entity_name: z.string().nullable(),
  created_by: z.string().nullable(),
  workspace_id: z.string().nullable(),
  total_dbus: z.number(),
  total_cost_usd: z.number(),
  total_requests: z.number(),
  total_input_tokens: z.number(),
  total_output_tokens: z.number(),
  unique_users: z.number(),
  days_active: z.number(),
  avg_requests_per_day: z.number(),
  avg_tokens_per_request: z.number(),
  input_token_pct: z.number(),
  output_token_pct: z.number(),
  first_request: z.string().nullable(),
  last_request: z.string().nullable(),
  usage_source: z.string().nullable(),
});

export const ModelServingEndpointTrendPointSchema = z.object({
  date: z.string(),
  dbus: z.number(),
  cost_usd: z.number(),
  requests: z.number(),
  input_tokens: z.number(),
  output_tokens: z.number(),
  unique_users: z.number(),
});

// Trend response is now a direct array (no wrapper)
export const ModelServingEndpointTrendResponseSchema = z.array(
  ModelServingEndpointTrendPointSchema
);

export type ModelServingEndpointDetail = z.infer<typeof ModelServingEndpointDetailSchema>;
export type ModelServingEndpointTrendPoint = z.infer<typeof ModelServingEndpointTrendPointSchema>;
export type ModelServingEndpointTrendResponse = z.infer<
  typeof ModelServingEndpointTrendResponseSchema
>;

export interface ModelServingEndpointDetailParams {
  endpoint_name: string;
  start_date?: string;
  end_date?: string;
}

// Flattened Vector Search detail schema (no nested objects, no data_availability)
export const VectorSearchEndpointDetailSchema = z.object({
  endpoint_name: z.string(),
  endpoint_id: z.string(),
  service: z.string(),
  usage_type: z.string(),
  workspace_id: z.string(),
  total_dbus: z.number(),
  total_cost_usd: z.number(),
  avg_daily_dbus: z.number(),
  days_active: z.number(),
  first_seen: z.string(),
  last_activity: z.string(),
});

export const VectorSearchEndpointTrendPointSchema = z.object({
  date: z.string(),
  dbus: z.number(),
  cost_usd: z.number(),
});

// Trend response is now a direct array (no wrapper)
export const VectorSearchEndpointTrendResponseSchema = z.array(
  VectorSearchEndpointTrendPointSchema
);

export type VectorSearchEndpointDetail = z.infer<typeof VectorSearchEndpointDetailSchema>;
export type VectorSearchEndpointTrendPoint = z.infer<typeof VectorSearchEndpointTrendPointSchema>;
export type VectorSearchEndpointTrendResponse = z.infer<
  typeof VectorSearchEndpointTrendResponseSchema
>;

export interface VectorSearchEndpointDetailParams {
  endpoint_name: string;
  start_date?: string;
  end_date?: string;
}

// AI Gateway detail schema — mirrors Vector Search minus `usage_type` (doesn't
// apply to gateway routes). Trend reuses the Vector Search trend point shape
// (`{ date, dbus, cost_usd }`) since the available billing signals are identical.
export const AiGatewayEndpointDetailSchema = z.object({
  endpoint_name: z.string(),
  endpoint_id: z.string(),
  service: z.string(),
  workspace_id: z.string(),
  total_dbus: z.number(),
  total_cost_usd: z.number(),
  avg_daily_dbus: z.number(),
  days_active: z.number(),
  first_seen: z.string(),
  last_activity: z.string(),
});

export const AiGatewayEndpointTrendResponseSchema = VectorSearchEndpointTrendResponseSchema;

export type AiGatewayEndpointDetail = z.infer<typeof AiGatewayEndpointDetailSchema>;
export type AiGatewayEndpointTrendResponse = z.infer<typeof AiGatewayEndpointTrendResponseSchema>;

export interface AiGatewayEndpointDetailParams {
  endpoint_name: string;
  start_date?: string;
  end_date?: string;
}

// Endpoint Callers Schemas (jobs/queries using the endpoint)
export const EndpointCallerItemSchema = z.object({
  job_id: z.string().nullable().optional(),
  job_name: z.string().nullable().optional(),
  notebook_id: z.string().nullable().optional(),
  executed_by: z.string().nullable().optional(),
  caller_type: z.string(),
  query_count: z.number().default(0),
  unique_queries: z.number().default(0),
  first_execution: z.string().nullable().optional(),
  last_execution: z.string().nullable().optional(),
  days_active: z.number().default(0),
  avg_queries_per_day: z.number().default(0),
});

export const EndpointCallersResponseSchema = z.object({
  callers: z.array(EndpointCallerItemSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
});

export type EndpointCallerItem = z.infer<typeof EndpointCallerItemSchema>;
export type EndpointCallersResponse = z.infer<typeof EndpointCallersResponseSchema>;

export interface EndpointCallersParams {
  endpoint_name: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// AI Function Usage Breakdown Schemas
export const AIFunctionUsageItemSchema = z.object({
  usage_type: z.string(),
  usage_identity: z.string(),
  job_name: z.string().nullable().optional(),
  executed_by: z.string().nullable().optional(),
  source_identifier: z.string().nullable().optional(),
  total_requests: z.number().default(0),
  days_active: z.number().default(0),
  last_used: z.string().nullable().optional(),
});

export const AIFunctionUsageBreakdownResponseSchema = z.object({
  usage: z.array(AIFunctionUsageItemSchema),
});

export type AIFunctionUsageItem = z.infer<typeof AIFunctionUsageItemSchema>;
export type AIFunctionUsageBreakdownResponse = z.infer<
  typeof AIFunctionUsageBreakdownResponseSchema
>;

export interface AIFunctionUsageBreakdownParams {
  endpoint_name: string;
  start_date?: string;
  end_date?: string;
}
